import { Search, SlidersHorizontal } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/common/Button';
import type { Action, State } from '../shared/useListReducer';
import { useFetchTraineeAssessmentResultProgramsQuery } from './TraineeReports.api';
import { AssessmentRunStatus, UserRole } from '@/apolloClient.types';
import Input from '@/components/common/Input';
import Select from '@/components/common/Select';
import { useGetCompanyId, useUser } from '@/contexts/UserSession';
import { useFetchGroupsQuery } from '@/pages/GroupManagementPage/GroupListing/GroupList.api';
import useCompanyRoleLabels from '@/utils/useCompanyRoleLabels';

export interface TraineeListFilterProp {
    state: State;
    dispatch: Dispatch<Action>;
}

const TraineeListFilter = ({ state, dispatch }: TraineeListFilterProp) => {
    const { t } = useTranslation('traineeReports');
    const { t: commonTranslation } = useTranslation('common');
    const selectedCompanyId = useGetCompanyId();
    const user = useUser(true);
    const currentUserId = user?.id;
    const [showFilters, setShowFilters] = useState(true);
    const { filter } = state;
    const [searchValue, setSearchValue] = useState<string>(filter.searchText ?? '');
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const { getRoleLabel } = useCompanyRoleLabels();

    useEffect(() => {
        setSearchValue(filter.searchText ?? '');
    }, [filter]);

    const onSearchClick = useCallback(() => {
        dispatch({ type: 'SET_FILTER', filter: { ...filter, searchText: searchValue || undefined } });
    }, [dispatch, filter, searchValue]);

    const onSearchChange = useCallback(
        (value: string) => {
            setSearchValue(value);
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
            searchDebounceRef.current = setTimeout(() => {
                dispatch({ type: 'SET_FILTER', filter: { ...filter, searchText: value || undefined } });
            }, 300);
        },
        [dispatch, filter]
    );

    const { data } = useFetchTraineeAssessmentResultProgramsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            companyId: selectedCompanyId || '',
            filter: {
                userIds: [currentUserId ?? ''],
                includeAssessmentRunStatuses: [AssessmentRunStatus.InProgress, AssessmentRunStatus.Ended],
            },
        },
        skip: !selectedCompanyId,
    });

    const programOptions = useMemo(() => {
        if (!data?.programs?.items) {
            return [];
        }

        return data.programs.items
            .map(p => {
                return {
                    value: p.id,
                    label: p.name,
                    assessmentRuns: p.assessmentRuns || [],
                };
            })
            .filter(p => p.assessmentRuns.length > 0);
    }, [data?.programs?.items]);

    // Fetch all company groups for global options when no program is selected
    const { data: groupsData } = useFetchGroupsQuery({
        variables: {
            companyId: selectedCompanyId || '',
        },
        skip: !selectedCompanyId,
        fetchPolicy: 'cache-and-network',
    });

    const assessmentRunOptions = useMemo(() => {
        return (
            programOptions
                .find(p => p.value === filter.programId)
                ?.assessmentRuns.map(ar => ({
                    value: ar.id,
                    label: ar.name,
                    groupDetail: ar.group,
                })) || []
        );
    }, [programOptions, filter.programId]);

    const isAdmin =
        user.isSuperAdmin ||
        (user?.__typename === 'TenantUser' &&
            (user.role === UserRole.CourseAdmin || user.role === UserRole.CompanyAdmin));

    const isTrainerPlus = isAdmin || (user?.__typename === 'TenantUser' && user.role === UserRole.Trainer);

    const groupOptions = useMemo(() => {
        // If program selected, derive groups from that program's runs
        if (filter.programId) {
            const selected = programOptions.find(p => p.value === filter.programId);
            if (!selected) {
                return [];
            }
            const groups: { value: string; label: string }[] = [];
            selected.assessmentRuns.forEach(run => {
                const group = run.group;
                if (group && !groups.find(item => item.value === group.id)) {
                    groups.push({ value: group.id, label: group.name });
                }
            });
            return groups;
        }

        // No program selected: show all company groups
        const allGroups = groupsData?.groups?.items || [];

        return allGroups.map(group => ({ value: group.id, label: group.name }));
    }, [filter.programId, programOptions, groupsData?.groups?.items]);

    const subgroupOptions = useMemo(() => {
        // If program selected, derive subgroups from company groups intersecting program's groups
        const allGroups = groupsData?.groups?.items || [];
        const programGroupIds = new Set<string>();
        if (filter.programId) {
            const selected = programOptions.find(p => p.value === filter.programId);
            selected?.assessmentRuns.forEach(ar => {
                if (ar.group?.id) {
                    programGroupIds.add(ar.group.id);
                }
            });
        }

        const sourceGroups = filter.programId
            ? allGroups.filter((g: any) => programGroupIds.has(g.id as string))
            : allGroups;

        const subgroups: { value: string; label: string; groupId: string }[] = [];
        sourceGroups.forEach((g: any) => {
            (g.subgroups || []).forEach((sg: any) => {
                if (!subgroups.find(s => s.value === sg.id)) {
                    subgroups.push({ value: sg.id as string, label: sg.name as string, groupId: g.id as string });
                }
            });
        });

        // If a group is selected, narrow to that group
        return subgroups.filter(sg => (filter.groupId ? sg.groupId === filter.groupId : true));
    }, [groupsData?.groups?.items, filter.programId, filter.groupId, programOptions]);

    const onProgramChange = useCallback(
        (value: string | number | null) =>
            dispatch({
                type: 'SET_FILTER',
                filter: {
                    ...filter,
                    programId: (value?.toString() as string) || undefined,
                    groupId: undefined,
                    subGroupId: undefined,
                    assessmentRunId: undefined,
                },
            }),
        [dispatch, filter]
    );

    const onGroupChange = useCallback(
        (value: string | number | null) =>
            dispatch({
                type: 'SET_FILTER',
                filter: {
                    ...filter,
                    groupId: (value?.toString() as string) || undefined,
                    subGroupId: undefined,
                    assessmentRunId: undefined,
                },
            }),
        [dispatch, filter]
    );

    const onSubgroupChange = useCallback(
        (value: string | number | null) =>
            dispatch({
                type: 'SET_FILTER',
                filter: {
                    ...filter,
                    subGroupId: (value?.toString() as string) || undefined,
                    assessmentRunId: undefined,
                },
            }),
        [dispatch, filter]
    );

    const onAssessmentRunChange = useCallback(
        (value: string | number | null) =>
            dispatch({
                type: 'SET_FILTER',
                filter: { ...filter, assessmentRunId: (value?.toString() as string) || undefined },
            }),
        [dispatch, filter]
    );

    const onClearFilter = useCallback(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
        setSearchValue('');
        dispatch({ type: 'SET_FILTER', filter: {} });
    }, [dispatch]);

    // Cleanup debounce on unmount to avoid stale dispatch
    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    const onSearchKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                if (searchDebounceRef.current) {
                    clearTimeout(searchDebounceRef.current);
                    searchDebounceRef.current = null;
                }
                dispatch({ type: 'SET_FILTER', filter: { ...filter, searchText: searchValue || undefined } });
            }
        },
        [dispatch, filter, searchValue]
    );

    const isTrainer = useMemo(() => user?.__typename === 'TenantUser' && user.role === UserRole.Trainer, [user]);

    const calculatedFilters = useMemo(() => {
        let count = 0;
        if (state.filter.programId) {
            count += 1;
        }
        if (state.filter.assessmentRunId) {
            count += 1;
        }
        if (state.filter.searchText) {
            count += 1;
        }
        if (!isTrainer && state.filter.groupId) {
            count += 1;
        }
        if (!isTrainer && state.filter.subGroupId) {
            count += 1;
        }
        return count;
    }, [
        isTrainer,
        state.filter.assessmentRunId,
        state.filter.groupId,
        state.filter.programId,
        state.filter.searchText,
        state.filter.subGroupId,
    ]);

    return (
        <div className="grid items-end gap-4 lg:grid-cols-[1fr_1fr_1fr_1fr_120px]">
            {isTrainerPlus && (
                <div className="mb-2 grid gap-4 lg:col-span-5 lg:grid-cols-[1fr_1fr_1fr_1fr_120px]">
                    <div className="lg:col-span-2">
                        <Input
                            name="trainee"
                            label={t('filter.search')}
                            onSuffixClick={onSearchClick}
                            suffixElement={<Search className="size-4 text-neutral-500" strokeWidth={2} />}
                            value={searchValue}
                            placeholder={t('filter.searchPlaceholder', {
                                role: getRoleLabel(UserRole.Trainee).toLocaleLowerCase(),
                            })}
                            onChange={e => onSearchChange(e.target.value)}
                            onKeyDown={onSearchKeyDown}
                        />
                    </div>

                    <div className="flex justify-self-end text-center font-semibold text-blue-600 lg:col-span-3 lg:px-4">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="flex items-center gap-1 font-semibold text-blue-600"
                        >
                            <SlidersHorizontal className="h-4 w-4" />
                            <span>{calculatedFilters}</span>
                            <span>
                                {calculatedFilters === 1
                                    ? commonTranslation('filter.filter')
                                    : commonTranslation('filter.filters')}
                            </span>
                        </button>
                    </div>
                </div>
            )}
            {showFilters && (
                <>
                    <div className={`grid ${!isAdmin ? 'lg:col-span-2' : 'lg:col-span-1'}`}>
                        <Select
                            name="program"
                            label={t('filter.program')}
                            placeholder={t('filter.programPlaceholder')}
                            options={programOptions}
                            value={filter.programId || null}
                            onChange={onProgramChange}
                        />
                    </div>
                    {isAdmin && (
                        <>
                            <div className="grid lg:col-span-1">
                                <Select
                                    name="group"
                                    label={t('filter.group')}
                                    placeholder={t('filter.groupPlaceholder')}
                                    options={groupOptions}
                                    value={filter.groupId || null}
                                    onChange={onGroupChange}
                                />
                            </div>
                            <div className="grid lg:col-span-1">
                                <Select
                                    name="subgroup"
                                    label={t('filter.subgroup')}
                                    placeholder={t('filter.subgroupPlaceholder')}
                                    options={subgroupOptions}
                                    value={filter.subGroupId || null}
                                    onChange={onSubgroupChange}
                                />
                            </div>
                        </>
                    )}
                    <div className={`grid ${!isAdmin ? 'lg:col-span-2' : 'lg:col-span-1'}`}>
                        <Select
                            name="assessmentRun"
                            label={t('filter.assessmentRun')}
                            placeholder={t('filter.assessmentRunPlaceholder')}
                            options={assessmentRunOptions}
                            value={filter.assessmentRunId || null}
                            onChange={onAssessmentRunChange}
                        />
                    </div>
                    <div className="justify-self-start lg:col-span-1">
                        <Button onClick={onClearFilter} className="w-full min-w-[120px] auto-cols-fr lg:w-auto" outline>
                            {commonTranslation('filter.clearAll')}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default TraineeListFilter;
